/**
 * UnderwaterMedium — PER-CHANNEL absorption for a submerged camera (R12 of
 * the 2026-09-25 water-realism wave).
 *
 * The underwater view used to be one monochrome `FogExp2` (every channel dims
 * toward one colour at one rate) under a veil. Real water removes RED with
 * path length first (Pope & Fry 1997: σₐ ≈ 0.45 /m at 650 nm, 0.057 at 550,
 * 0.009 at 450) and glows blue-green with in-scattered sun. Per pixel, from
 * the depth buffer's view distance d:
 *
 *     out = scene · T(d) + L_in · (1 − T(d)),   T = exp(−σₜ·d) per channel
 *
 * A per-channel multiplier needs multiplicative blending, and an additive term
 * a second pass — two fullscreen clip-space triangles drawn after everything
 * (the UnderwaterOverlay pattern, never a scene copy):
 *   1. ATTENUATE — CustomBlending (Zero, OneMinusSrcColor): out = dst·(1 − src)
 *      with src = 1 − T. Marked as an MRT overlay: its zero writes into the
 *      normal / velocity / … targets leave them UNCHANGED under this factor
 *      (dst·(1 − 0)) — a Multiply blend would have zeroed them.
 *   2. IN-SCATTER — additive (One, One): src = L_in·(1 − T); zeros elsewhere.
 * `L_in` (the medium's glow — σₛ/σₜ × the sun reaching the camera's depth) is
 * a uniform the owner computes on the CPU (waterLook.underwaterComposite is
 * the oracle). The depth buffer is read through `viewportDepthTexture` (the
 * soft-particle path — one copy per frame, both backends).
 *
 * THE WATERLINE (shader gaps R1, 2026-09-29): a camera whose near plane the water surface cuts sees air above the
 * line where the surface meets the near plane and water below it. `uWaterline` = (l0, l1, l2, mode): mode 0 = the
 * whole view is under water (the old switch — output unchanged); mode 1 = only pixels with
 * d(u, v) = l0 + l1·u + l2·v < 0 (top-origin screen UV, screen heights) take the medium, antialiased over
 * ±`uWaterlineEdge`. `waterLook.nearPlaneWaterline` computes the line, `waterlineCover` is the oracle.
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../../render/post/mrtOverlay.js';
import {
  Fn,
  cameraFar,
  cameraNear,
  exp,
  float,
  mix,
  perspectiveDepthToViewZ,
  smoothstep,
  positionLocal,
  screenUV,
  uniform,
  vec3,
  vec4,
  viewportDepthTexture,
} from 'three/tsl';
import { extinction, WATERLINE_EDGE } from './waterLook.js';

export interface UnderwaterMediumConfig {
  /** Extinction σₜ per channel (1/m). Default Pope & Fry σₐ + a coastal σₛ. */
  extinction?: { r: number; g: number; b: number };
  /** Distance cap (m) — the sky / far plane saturate to pure in-scatter. Default 400. */
  maxDistance?: number;
}

export class UnderwaterMedium {
  /** [attenuate, in-scatter] — add both to the scene; toggle `visible` together. */
  readonly meshes: [THREE.Mesh, THREE.Mesh];
  /** Extinction per channel (1/m). */
  readonly uSigma = uniform(new THREE.Vector3(0.49, 0.102, 0.059));
  /** The medium's in-scattered radiance (linear HDR) — the owner drives it from the sun and the camera depth. */
  readonly uInscatter = uniform(new THREE.Vector3(0.0, 0.03, 0.04));
  /** Master fade 0..1. */
  readonly uIntensity = uniform(1);
  /** The near-plane waterline (l0, l1, l2, mode) — mode 0 = the whole view (default), 1 = only below the line. */
  readonly uWaterline = uniform(new THREE.Vector4(0, 0, 0, 0));
  /** The water / air edge's half-width in screen heights. */
  readonly uWaterlineEdge = uniform(WATERLINE_EDGE);

  constructor(config: UnderwaterMediumConfig = {}) {
    const st = config.extinction ?? (() => { const e = extinction(); return { r: e[0], g: e[1], b: e[2] }; })();
    this.uSigma.value.set(st.r, st.g, st.b);
    const maxD = config.maxDistance ?? 400;

    const tri = (): THREE.BufferGeometry => {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
      // Dummy normals — TSL's normal accessor warns (then zero-fills) without one.
      g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 0, 1, 0, 0, 1]), 3));
      return g;
    };
    const uSigma = this.uSigma, uIn = this.uInscatter, uI = this.uIntensity;
    // View distance (m) of the opaque surface behind this pixel; the sky's clear
    // depth maps to the far plane, capped (T → 0: pure medium, as in real water).
    // ONE depth node shared by both passes: a ViewportTextureNode copies the
    // framebuffer once per render PER NODE INSTANCE, right before the first
    // object using it draws — shared, the two passes cost one copy, taken after
    // the water surface is in the depth buffer (both passes draw last).
    const depthTex = viewportDepthTexture(screenUV);
    const viewDist = Fn(() => {
      const z = perspectiveDepthToViewZ(depthTex.x, cameraNear, cameraFar).negate();
      return z.clamp(0, maxD);
    });
    const trans = Fn(() => exp(vec3(uSigma).negate().mul(viewDist())));
    // The water cover of this pixel: 1 in mode 0 (the whole view), else the side of the near-plane waterline.
    const uWl = this.uWaterline, uWlE = this.uWaterlineEdge;
    const cover = Fn(() => {
      const d = uWl.x.add(uWl.y.mul(screenUV.x)).add(uWl.z.mul(screenUV.y));
      const under = float(1).sub(smoothstep(uWlE.negate(), uWlE, d));
      return mix(float(1), under, uWl.w);
    });

    const mk = (colorNode: unknown, blend: 'attenuate' | 'inscatter', order: number, name: string): THREE.Mesh => {
      const m = new THREE.MeshBasicNodeMaterial();
      m.transparent = true;
      m.depthTest = false;
      m.depthWrite = false;
      m.fog = false;
      m.toneMapped = false;
      if (blend === 'attenuate') {
        m.blending = THREE.CustomBlending;
        m.blendEquation = THREE.AddEquation;
        m.blendSrc = THREE.ZeroFactor;
        m.blendDst = THREE.OneMinusSrcColorFactor;
        m.blendSrcAlpha = THREE.ZeroFactor;
        m.blendDstAlpha = THREE.OneFactor;
      } else {
        m.blending = THREE.CustomBlending;
        m.blendEquation = THREE.AddEquation;
        m.blendSrc = THREE.OneFactor;
        m.blendDst = THREE.OneFactor;
        m.blendSrcAlpha = THREE.ZeroFactor;
        m.blendDstAlpha = THREE.OneFactor;
      }
      markMrtOverlay(m); // a screen term, not a surface: the G-buffer behind it stays
      m.vertexNode = Fn(() => vec4(positionLocal.x, positionLocal.y, 0, 1))();
      m.colorNode = colorNode as never;
      const mesh = new THREE.Mesh(tri(), m);
      mesh.name = name;
      mesh.frustumCulled = false;
      mesh.renderOrder = order;
      mesh.visible = false;
      return mesh;
    };
    const attenuate = mk(Fn(() => {
      const t = trans();
      // src = (1 − T)·intensity → out = dst·(1 − src)
      return vec4(vec3(1).sub(t).mul(uI).mul(cover()), 0);
    })(), 'attenuate', 9996, 'UnderwaterMediumAttenuate');
    const inscatter = mk(Fn(() => {
      const t = trans();
      return vec4(vec3(uIn).mul(vec3(1).sub(t)).mul(uI).mul(cover()), 0);
    })(), 'inscatter', 9997, 'UnderwaterMediumInscatter');
    this.meshes = [attenuate, inscatter];
    void float;
  }

  /** The near-plane waterline (waterLook.nearPlaneWaterline's `line`), or null for the whole view. */
  setWaterline(line: readonly [number, number, number] | null): void {
    if (line) this.uWaterline.value.set(line[0], line[1], line[2], 1);
    else this.uWaterline.value.set(0, 0, 0, 0);
  }

  set visible(v: boolean) { for (const m of this.meshes) m.visible = v; }
  get visible(): boolean { return this.meshes[0].visible; }

  dispose(): void {
    for (const m of this.meshes) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
  }
}
